import { routeRegistry } from './openapi/registry.js';
/**
 * Status codes treated as the operation's canonical "success" response.
 * `submissions_create` returns 202 (queued); every other operation returns
 * 200. Both are joined; the first response with a `schema` wins so that
 * `submissions_create` resolves to `createSubmissionResponseSchema` rather
 * than to nothing.
 */
const SUCCESS_STATUSES = ['200', '202'];
function pickSuccessResponse(responses) {
    for (const status of SUCCESS_STATUSES) {
        const r = responses[status];
        if (r?.schema)
            return r;
    }
    return undefined;
}
/**
 * `operationId` → Zod schema for the canonical success response body.
 *
 * Excludes operations whose success response has no body (e.g. 204) or that
 * are not in `SUCCESS_STATUSES`. Callers must guard on presence — the
 * `Partial` wrapper makes that explicit at the type level.
 */
export const operationResponseSchemas = Object.freeze(Object.fromEntries(routeRegistry.flatMap((route) => {
    const r = pickSuccessResponse(route.responses);
    return r?.schema ? [[route.operationId, r.schema]] : [];
})));
function compileTemplate(pathTemplate) {
    // Escape regex metacharacters, then translate `{name}` to a path segment
    // capture. OpenAPI placeholders are bounded by `/` or the string end, so
    // `[^/]+` is the right segment matcher.
    const escaped = pathTemplate.replace(/[.+^$()|[\]\\]/g, '\\$&');
    const withParams = escaped.replace(/\{[^}]+\}/g, '[^/]+');
    return new RegExp(`^${withParams}$`);
}
function specificityScore(pathTemplate) {
    // Returns the placeholder count. Fewer placeholders = more specific.
    // The COMPILED_ROUTES sort below is ascending, so a fully-static path
    // (count 0) sorts ahead of any templated sibling.
    const placeholders = (pathTemplate.match(/\{[^}]+\}/g) ?? []).length;
    return placeholders;
}
const COMPILED_ROUTES = Object.freeze(routeRegistry
    .slice()
    .sort((a, b) => specificityScore(a.path) - specificityScore(b.path))
    .map((route) => ({
    method: route.method.toUpperCase(),
    re: compileTemplate(route.path),
    operationId: route.operationId,
    anonymous: route.anonymous ?? false,
})));
/**
 * Resolve `(method, urlPath)` to an `operationId`. Returns `undefined` for
 * paths that don't match any registered route — the mutator MUST treat
 * unknown ops as silent (no warning), since they're either dev-only stub
 * paths or genuine drift the audit gate already handles.
 *
 * `urlPath` is the bare path component (no querystring, no origin).
 */
export function resolveOperationId(method, urlPath) {
    const m = method.toUpperCase();
    for (const r of COMPILED_ROUTES) {
        if (r.method === m && r.re.test(urlPath))
            return r.operationId;
    }
    return undefined;
}
/**
 * Resolve `(method, urlPath)` to whether the matched route is declared
 * anonymous in the contract (`routeRegistry[].anonymous`). Returns `false`
 * for paths that match no registered route — unknown paths are treated as
 * authenticated (deny-by-default), matching the API gate's posture.
 *
 * `urlPath` is the bare path component (no querystring, no origin).
 */
export function isAnonymousOperation(method, urlPath) {
    const m = method.toUpperCase();
    for (const r of COMPILED_ROUTES) {
        if (r.method === m && r.re.test(urlPath))
            return r.anonymous;
    }
    return false;
}
//# sourceMappingURL=operation-registry.js.map